// @story: libs/storybook/stories/components/input/button.stories.ts
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { interval, map, of, startWith } from 'rxjs';
import { PermissionService, UsersMeService } from '@zag/auth/ui';
import { FormattingService, ToastService, ZagButton, ZagIcon, ZagTimePipe } from '@zag/ui';
import type { ClockState } from '@zag/timetracking/common';
import { ClockStampService } from './clock-stamp.service';
import { ClockStampEventsService } from './clock-stamp-events.service';

const STAMP_GUARD_MS = 60_000;

@Component({
  selector: 'sc-clock-stamp-button',
  host: { '[class.clock-stamp--show-on-small-screens]': 'showOnSmallScreens()' },
  imports: [ZagButton, ZagIcon, ZagTimePipe, TranslatePipe],
  template: `
    @if (visible()) {
      <span class="clock-stamp-wrap">
        <button
          zagButton="bare"
          type="button"
          class="clock-stamp"
          [class.clock-stamp--in]="isClockedIn()"
          [disabled]="!canStamp() || mutating() || withinStampGuard() || todayIsLocked()"
          [attr.aria-label]="actionLabel()"
          [attr.title]="tooltip()"
          (click)="toggle()"
        >
          <span [zagIcon]="isClockedIn() ? 'stop' : 'play_arrow'"></span>
          @if (isClockedIn() && startedAtDate(); as started) {
            <span class="clock-stamp__runtime" aria-hidden="true"> {{ 'clockStamp.sinceLabel' | translate }} {{ started | zagTime }} </span>
          }
        </button>
      </span>
    }
  `,
  styles: [
    `
      :host {
        display: inline-flex;
        background: transparent;
        border: none;
        box-shadow: none;
      }
      .clock-stamp-wrap {
        position: relative;
        display: inline-flex;
      }
      .clock-stamp {
        display: inline-flex;
        align-items: center;
        gap: var(--spacing-xs);
        padding: 0 var(--spacing-sm);
        background: transparent;
        border: none;
        box-shadow: none;
        color: var(--text-color-secondary);
        cursor: pointer;
        flex-shrink: 0;

        &:hover:not(:disabled) {
          color: var(--color-primary);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        span[zagIcon] {
          font-size: var(--icon-size-action);
        }
      }

      .clock-stamp--in {
        color: var(--color-success, var(--color-primary));
      }

      .clock-stamp__runtime {
        font-size: var(--font-size-xs);
        font-weight: 600;
        font-variant-numeric: tabular-nums;
      }

      /* The topbar hides secondary actions on the smallest viewports (same as the AI button). */
      @media (max-width: 539px) {
        .clock-stamp {
          display: none;
        }

        :host(.clock-stamp--show-on-small-screens) .clock-stamp {
          display: inline-flex;
        }

        /* Footer column too narrow for icon + runtime side by side. */
        :host-context(.app-mobile-bottom-nav) .clock-stamp {
          flex-direction: column;
          gap: 0;
          padding: 0;
          line-height: 1;
        }

        :host-context(.app-mobile-bottom-nav) .clock-stamp__runtime {
          white-space: nowrap;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClockStampButtonComponent {
  private readonly clockService = inject(ClockStampService);
  private readonly stampEvents = inject(ClockStampEventsService);
  private readonly usersMeService = inject(UsersMeService);
  private readonly permissions = inject(PermissionService);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);
  private readonly formatting = inject(FormattingService);

  protected readonly mutating = signal(false);
  readonly showOnSmallScreens = input(false);
  /** Server truth after the last mutation — wins over the initial resource load. */
  private readonly overrideState = signal<ClockState | null>(null);

  private readonly hasLinkedPerson = computed(() => this.usersMeService.profile()?.linkedPerson?.id !== undefined);
  readonly canStamp = computed(() => this.permissions.hasPermission('timetracking:my-week:confirm'));

  private readonly stateResource = rxResource<ClockState | null, boolean>({
    params: () => this.hasLinkedPerson(),
    stream: ({ params: linked }) => (linked ? this.clockService.getState() : of(null)),
  });

  // value() rethrows in error state — guard with hasValue() to avoid breaking change detection.
  readonly state = computed<ClockState | null>(
    () => this.overrideState() ?? (this.stateResource.hasValue() ? (this.stateResource.value() ?? null) : null)
  );
  readonly visible = computed(() => this.hasLinkedPerson());
  readonly isClockedIn = computed(() => this.state()?.isClockedIn ?? false);

  /** Ticks every 5 s so the stamp guard flips off once the elapsed window passes. */
  private readonly nowMs = toSignal(
    interval(5_000).pipe(
      startWith(0),
      map(() => Date.now())
    ),
    { initialValue: Date.now() }
  );

  readonly todayIsLocked = computed(() => this.stampEvents.todayLocked());

  /** Blocks stamping until 60,000 ms have elapsed since the observed last stamp (§3h.49). */
  readonly withinStampGuard = computed(() => {
    const lastAt = this.state()?.lastStamp?.at;
    if (!lastAt) return false;
    const lastTs = new Date(lastAt).getTime();
    if (Number.isNaN(lastTs)) return false;
    return this.nowMs() - lastTs < STAMP_GUARD_MS;
  });

  /** Start of the open stamp as a real UTC instant, ready for tenant-local formatting. */
  readonly startedAtDate = computed(() => {
    const startedAt = this.state()?.openStamp?.startedAt;
    return startedAt ? new Date(startedAt) : null;
  });

  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });

  readonly actionLabel = computed(() => {
    this.currentLang();
    return this.translate.instant(this.isClockedIn() ? 'clockStamp.clockOut' : 'clockStamp.clockIn');
  });

  readonly tooltip = computed(() => {
    this.currentLang();
    if (this.todayIsLocked()) return this.translate.instant('clockStamp.dayLocked');
    if (this.withinStampGuard()) return this.translate.instant('clockStamp.tooSoon');
    const last = this.state()?.lastStamp;
    if (!last) return this.actionLabel();
    const at = new Date(last.at);
    // Clock stamps are real UTC (SELECT now() in the DB), format in local time.
    const lastLine = this.translate.instant(last.type === 'in' ? 'clockStamp.lastStampIn' : 'clockStamp.lastStampOut', {
      date: this.formatting.formatDate(at),
      time: this.formatting.formatTime(at),
    });
    return `${this.actionLabel()} — ${lastLine}`;
  });

  toggle(): void {
    if (!this.canStamp() || !this.hasLinkedPerson() || this.mutating() || this.withinStampGuard() || this.todayIsLocked()) return;
    if (this.state() === null) {
      if (this.stateResource.error() !== undefined) this.stateResource.reload();
      return;
    }
    this.executeStamp(this.isClockedIn());
  }

  protected executeStamp(clockedIn: boolean, clockInBody: { homeOffice?: boolean; note?: string | null } = {}): void {
    if (this.mutating()) return;
    this.mutating.set(true);
    this.overrideState.set(optimisticToggle(this.state(), clockedIn));
    const request = clockedIn ? this.clockService.clockOut() : this.clockService.clockIn(clockInBody);
    request.subscribe({
      next: state => {
        this.mutating.set(false);
        this.overrideState.set(state);
        this.toast.success(this.resolveSuccessToast(clockedIn, state));
        this.stampEvents.notifyStampChanged();
      },
      error: (error: unknown) => {
        this.mutating.set(false);
        this.overrideState.set(null);
        this.stateResource.reload();
        if (error instanceof HttpErrorResponse && error.status === 409) {
          this.toast.error(this.translate.instant(clockedIn ? 'clockStamp.conflictOut' : 'clockStamp.conflictIn'));
        } else {
          this.toast.error(this.translate.instant('clockStamp.error'));
        }
      },
    });
  }

  private resolveSuccessToast(wasClockedIn: boolean, state: ClockState): string {
    if (wasClockedIn) return this.translate.instant('clockStamp.clockedOut');
    const startedAt = state.openStamp?.startedAt;
    if (!startedAt) return this.translate.instant('clockStamp.clockedIn');
    return this.translate.instant('clockStamp.clockedInAt', {
      time: this.formatting.formatTime(new Date(startedAt)),
    });
  }
}

/** Local optimistic state until the server responds. */
export function optimisticToggle(state: ClockState | null, wasClockedIn: boolean): ClockState {
  const nowIso = new Date(Date.now()).toISOString();
  if (wasClockedIn) {
    return { isClockedIn: false, openStamp: null, lastStamp: { type: 'out', at: nowIso } };
  }
  return {
    isClockedIn: true,
    openStamp: { bookingId: state?.openStamp?.bookingId ?? '', startedAt: nowIso },
    lastStamp: { type: 'in', at: nowIso },
  };
}
